import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown, Download, Filter, Search, Clock, Settings, X, Plus, FileText } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear, endOfYear, subDays, startOfDay, endOfDay, addDays, parseISO, isValid, getDaysInMonth, setDate, addMonths } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import PayrollResourceSummary from '../components/payroll/PayrollResourceSummary';
import PayrollEditModal from '../components/payroll/PayrollEditModal';
import PayrollRequestsSection from '../components/payroll/PayrollRequestsSection';
import { getPayrollEntries, updatePayrollEntry, deletePayrollEntry } from '../lib/payroll';
import { savePayrollSettings, getPayrollSettings, type PayrollSettings } from '../lib/payrollSettings';
import toast from 'react-hot-toast';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  resource_avatar_url?: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
  booking?: {
    id: string;
    business_id: string;
    booking_number: number;
    title: string;
    status: string;
    created_at: string;
    end_date: string;
    end_time: string;
    completed_at: string;
  };
  resource?: {
    first_name: string;
    last_name: string;
    email: string;
    flat_rate_amount: number | null;
    hourly_rate: number | null;
    compensation_type: string;
    avatar_url?: string;
  };
}

// Add getCurrentPeriodRange function before DATE_RANGES
const getCurrentPeriodRange = (
  frequency: string, 
  startDate: string,
  customPeriod1Start?: string,
  customPeriod1End?: string,
  customPeriod2Start?: string,
  customPeriod2End?: string
) => {
  const today = new Date();
  const currentDay = today.getDate();
  
  switch (frequency) {
    case 'weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentWeekOffset = Math.floor(daysSinceStart / 7) * 7;
      const currentPeriodStart = addDays(periodStart, currentWeekOffset);
      const currentPeriodEnd = addDays(currentPeriodStart, 6);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'bi-weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentPeriodOffset = Math.floor(daysSinceStart / 14) * 14;
      const currentPeriodStart = addDays(periodStart, currentPeriodOffset);
      const currentPeriodEnd = addDays(currentPeriodStart, 13);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'semi-monthly': {
      if (currentDay <= 15) {
        return {
          start: setDate(startOfMonth(today), 1),
          end: setDate(startOfMonth(today), 15)
        };
      } else {
        return {
          start: setDate(startOfMonth(today), 16),
          end: endOfMonth(today)
        };
      }
    }
    
    case 'monthly': {
      const periodStart = parseISO(startDate);
      const startDay = periodStart.getDate();
      let currentPeriodStart: Date;
      
      if (currentDay >= startDay) {
        currentPeriodStart = setDate(today, startDay);
      } else {
        currentPeriodStart = setDate(subMonths(today, 1), startDay);
      }
      
      const currentPeriodEnd = addDays(setDate(addDays(currentPeriodStart, 32), startDay), -1);
      return { start: currentPeriodStart, end: currentPeriodEnd };
    }
    
    case 'custom': {
      if (!customPeriod1Start || !customPeriod1End || !customPeriod2Start || !customPeriod2End) {
        return { start: startOfMonth(today), end: endOfMonth(today) };
      }
      
      const p1Start = parseInt(customPeriod1Start);
      const p1End = parseInt(customPeriod1End);
      const p2Start = parseInt(customPeriod2Start);
      const p2End = parseInt(customPeriod2End);
      
      console.log('[PAYROLL PERIOD] Current period calculation:', {
        today: format(today, 'yyyy-MM-dd'),
        currentDay,
        p1Start,
        p1End,
        p2Start,
        p2End
      });
      
      // Handle period crossing month boundary (e.g., 26th-10th)
      if (p1Start > p1End) {
        if (currentDay >= p1Start || currentDay <= p1End) {
          const periodStart = currentDay >= p1Start 
            ? setDate(today, p1Start)
            : setDate(subMonths(today, 1), p1Start);
          const periodEnd = currentDay >= p1Start
            ? setDate(addMonths(today, 1), p1End)
            : setDate(today, p1End);
          const result = { start: periodStart, end: periodEnd };
          console.log('[PAYROLL PERIOD] Current period (P1 crosses months):', {
            start: format(result.start, 'yyyy-MM-dd'),
            end: format(result.end, 'yyyy-MM-dd')
          });
          return result;
        }
      }
      
      if (p2Start > p2End) {
        if (currentDay >= p2Start || currentDay <= p2End) {
          const periodStart = currentDay >= p2Start
            ? setDate(today, p2Start)
            : setDate(subMonths(today, 1), p2Start);
          const periodEnd = currentDay >= p2Start
            ? setDate(addMonths(today, 1), p2End)
            : setDate(today, p2End);
          const result = { start: periodStart, end: periodEnd };
          console.log('[PAYROLL PERIOD] Current period (P2 crosses months):', {
            start: format(result.start, 'yyyy-MM-dd'),
            end: format(result.end, 'yyyy-MM-dd')
          });
          return result;
        }
      }
      
      // Regular periods within same month
      if (currentDay >= p1Start && currentDay <= p1End) {
        const result = {
          start: setDate(today, p1Start),
          end: setDate(today, p1End)
        };
        console.log('[PAYROLL PERIOD] Current period (P1 same month):', {
          start: format(result.start, 'yyyy-MM-dd'),
          end: format(result.end, 'yyyy-MM-dd')
        });
        return result;
      } else {
        const result = {
          start: setDate(today, p2Start),
          end: setDate(today, p2End)
        };
        console.log('[PAYROLL PERIOD] Current period (P2 same month):', {
          start: format(result.start, 'yyyy-MM-dd'),
          end: format(result.end, 'yyyy-MM-dd')
        });
        return result;
      }
    }
    
    default:
      return { start: startOfMonth(today), end: endOfMonth(today) };
  }
};

// Update the getLastPeriodRange function with a corrected custom period calculation
const getLastPeriodRange = (
  frequency: string, 
  startDate: string,
  customPeriod1Start?: string,
  customPeriod1End?: string,
  customPeriod2Start?: string,
  customPeriod2End?: string
) => {
  const today = new Date();
  const currentDay = today.getDate();
  
  switch (frequency) {
    // Weekly, bi-weekly, semi-monthly, and monthly are unchanged
    case 'weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentWeekOffset = Math.floor(daysSinceStart / 7) * 7;
      
      // Last period is 7 days before current period
      const lastPeriodStart = addDays(periodStart, currentWeekOffset - 7);
      const lastPeriodEnd = addDays(lastPeriodStart, 6);
      return { start: lastPeriodStart, end: lastPeriodEnd };
    }
    
    case 'bi-weekly': {
      const periodStart = parseISO(startDate);
      const daysSinceStart = Math.floor((today.getTime() - periodStart.getTime()) / (24 * 60 * 60 * 1000));
      const currentPeriodOffset = Math.floor(daysSinceStart / 14) * 14;
      
      // Last period is 14 days before current period
      const lastPeriodStart = addDays(periodStart, currentPeriodOffset - 14);
      const lastPeriodEnd = addDays(lastPeriodStart, 13);
      return { start: lastPeriodStart, end: lastPeriodEnd };
    }
    
    case 'semi-monthly': {
      // For semi-monthly, check which half of the month we're in
      if (currentDay <= 15) {
        // We're in the first half, so last period is second half of previous month
        const lastMonth = subMonths(today, 1);
        return {
          start: setDate(startOfMonth(lastMonth), 16),
          end: endOfMonth(lastMonth)
        };
      } else {
        // We're in the second half, so last period is first half of current month
        return {
          start: setDate(startOfMonth(today), 1),
          end: setDate(startOfMonth(today), 15)
        };
      }
    }
    
    case 'monthly': {
      const periodStart = parseISO(startDate);
      const startDay = periodStart.getDate();
      let currentPeriodStart: Date;
      
      if (currentDay >= startDay) {
        currentPeriodStart = setDate(today, startDay);
      } else {
        currentPeriodStart = setDate(subMonths(today, 1), startDay);
      }
      
      // Last period starts one month before current period start
      const lastPeriodStart = subMonths(currentPeriodStart, 1);
      const lastPeriodEnd = addDays(setDate(addDays(lastPeriodStart, 32), startDay), -1);
      
      return { start: lastPeriodStart, end: lastPeriodEnd };
    }
    
    case 'custom': {
      // If essential settings are missing, return last month as a fallback
      if (!customPeriod1Start || !customPeriod1End || !customPeriod2Start || !customPeriod2End) {
        return { start: startOfMonth(subMonths(today, 1)), end: endOfMonth(subMonths(today, 1)) };
      }
      
      const p1Start = parseInt(customPeriod1Start);
      const p1End = parseInt(customPeriod1End);
      const p2Start = parseInt(customPeriod2Start);
      const p2End = parseInt(customPeriod2End);
      
      console.log('[PAYROLL PERIOD] Last period calculation:', {
        today: format(today, 'yyyy-MM-dd'),
        currentDay,
        p1Start,
        p1End,
        p2Start,
        p2End
      });
      
      // Determine which period we're currently in
      let inPeriod1 = false;
      let inPeriod2 = false;
      
      // Check if period 1 crosses month boundary (e.g., 26th to 10th)
      if (p1Start > p1End) {
        if (currentDay >= p1Start || currentDay <= p1End) {
          inPeriod1 = true;
        }
      } 
      // Period 1 is within same month
      else if (currentDay >= p1Start && currentDay <= p1End) {
        inPeriod1 = true;
      }
      
      // Check if period 2 crosses month boundary
      if (p2Start > p2End) {
        if (currentDay >= p2Start || currentDay <= p2End) {
          inPeriod2 = true;
        }
      }
      // Period 2 is within same month
      else if (currentDay >= p2Start && currentDay <= p2End) {
        inPeriod2 = true;
      }
      
      // If we're in period 1, the previous period is period 2 from the previous cycle
      if (inPeriod1) {
        console.log('[PAYROLL PERIOD] Currently in period 1');
        
        // We're in period 1, so last period is period 2 from the previous cycle
        
        // If period 1 crosses month boundary (e.g., May 26 - June 10)
        if (p1Start > p1End) {
          // We're in the early part of the month (e.g., June 10)
          // Previous period 2 is in the previous month (e.g., May 11 - May 25)
          const lastMonth = subMonths(today, 1);
          const result = {
            start: setDate(lastMonth, p2Start),
            end: setDate(lastMonth, p2End)
          };
          console.log('[PAYROLL PERIOD] Last period (P1 crosses months):', {
            start: format(result.start, 'yyyy-MM-dd'),
            end: format(result.end, 'yyyy-MM-dd')
          });
          return result;
        } 
        // Period 1 is within same month
        else {
          // Previous period 2 is also in previous month
          const lastMonth = subMonths(today, 1);
          const result = {
            start: setDate(lastMonth, p2Start),
            end: setDate(lastMonth, p2End)
          };
          console.log('[PAYROLL PERIOD] Last period (P1 same month):', {
            start: format(result.start, 'yyyy-MM-dd'),
            end: format(result.end, 'yyyy-MM-dd')
          });
          return result;
        }
      }
      
      // If we're in period 2, the previous period is period 1 (which might cross from previous month)
      else if (inPeriod2) {
        console.log('[PAYROLL PERIOD] Currently in period 2');
        
        // We're in period 2, so last period is period 1
        
        // If period 1 crosses month boundary (e.g., May 26 - June 10)
        if (p1Start > p1End) {
          // Period 1 starts in previous month and ends in current month
          const lastMonth = subMonths(today, 1);
          const result = {
            start: setDate(lastMonth, p1Start),
            end: setDate(today, p1End)
          };
          console.log('[PAYROLL PERIOD] Last period (P1 crosses months):', {
            start: format(result.start, 'yyyy-MM-dd'),
            end: format(result.end, 'yyyy-MM-dd')
          });
          return result;
        } 
        // Period 1 is within same month - it's in current month
        else {
          const result = {
            start: setDate(today, p1Start),
            end: setDate(today, p1End)
          };
          console.log('[PAYROLL PERIOD] Last period (P1 same month):', {
            start: format(result.start, 'yyyy-MM-dd'),
            end: format(result.end, 'yyyy-MM-dd')
          });
          return result;
        }
      }
      
      // Fallback if we're somehow not in any period
      // Just get the previous month as a fallback
      console.warn("Could not determine current pay period - using fallback");
      return { 
        start: startOfMonth(subMonths(today, 1)), 
        end: endOfMonth(subMonths(today, 1)) 
      };
    }
    
    default:
      return { start: startOfMonth(subMonths(today, 1)), end: endOfMonth(subMonths(today, 1)) };
  }
};

const DATE_RANGES = [
  { label: 'Current Period', value: 'current_period', getRange: () => {
    // This will be updated when we access the current payroll settings
    return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
  }},
  { label: 'Last Period', value: 'last_period', getRange: () => {
    // This will be updated when we access the current payroll settings
    return { start: startOfMonth(subMonths(new Date(), 1)), end: endOfMonth(subMonths(new Date(), 1)) };
  }},
  { label: 'Today', value: 'today', getRange: () => {
    const today = new Date();
    return { start: startOfDay(today), end: endOfDay(today) };
  }},
  { label: 'Yesterday', value: 'yesterday', getRange: () => {
    const yesterday = subDays(new Date(), 1);
    return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
  }},
  { label: 'Select Day', value: 'select_day', getRange: (selectedDay?: Date) => {
    const day = selectedDay || new Date();
    return { start: startOfDay(day), end: endOfDay(day) };
  }},
  { label: 'Last 30 days', value: 'last_30_days', getRange: () => ({
    start: subDays(new Date(), 29),
    end: new Date()
  })},
  { label: 'This Month', value: 'this_month', getRange: () => ({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  })},
  { label: 'Last Month', value: 'last_month', getRange: () => {
    const lastMonth = subMonths(new Date(), 1);
    return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
  }},
  { label: 'Custom Range', value: 'custom_range', getRange: (customStartDate?: Date, customEndDate?: Date) => ({
    start: customStartDate || startOfMonth(new Date()),
    end: customEndDate || endOfMonth(new Date())
  })}
];

export default function ManagePayroll() {
  const { selectedBusiness } = useBusiness();
  const [entries, setEntries] = useState<PayrollEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRange, setSelectedRange] = useState(DATE_RANGES.find(r => r.value === 'current_period')!);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCustomRangePickerOpen, setIsCustomRangePickerOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [expandedResources, setExpandedResources] = useState<Set<string>>(new Set());
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [dateRange, setDateRange] = useState(selectedRange.getRange());
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isPayrollRequestsModalOpen, setIsPayrollRequestsModalOpen] = useState(false);
  
  // Payroll period settings
  const [payrollFrequency, setPayrollFrequency] = useState('bi-weekly');
  const [payrollStartDate, setPayrollStartDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Add new state variables after other state declarations
  const [customPeriod1Start, setCustomPeriod1Start] = useState('26');
  const [customPeriod1End, setCustomPeriod1End] = useState('10');
  const [customPeriod2Start, setCustomPeriod2Start] = useState('11');
  const [customPeriod2End, setCustomPeriod2End] = useState('25');

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<PayrollEntry | null>(null);
  
  useEffect(() => {
    // Update date range when selection changes
    if (selectedRange.value === 'select_day') {
      setDateRange(selectedRange.getRange(selectedDay || new Date()));
    } else {
      setDateRange(selectedRange.getRange());
    }
  }, [selectedRange, selectedDay]);

  // Update the date range calculation for Current Period
  useEffect(() => {
    if (selectedRange.value === 'current_period') {
      const currentPeriod = getCurrentPeriodRange(
        payrollFrequency,
        payrollStartDate,
        customPeriod1Start,
        customPeriod1End,
        customPeriod2Start,
        customPeriod2End
      );
      setDateRange(currentPeriod);
    } else if (selectedRange.value === 'last_period') {
      const lastPeriod = getLastPeriodRange(
        payrollFrequency,
        payrollStartDate,
        customPeriod1Start,
        customPeriod1End,
        customPeriod2Start,
        customPeriod2End
      );
      setDateRange(lastPeriod);
    } else if (selectedRange.value === 'select_day') {
      setDateRange(selectedRange.getRange(selectedDay || new Date()));
    } else if (selectedRange.value === 'custom_range') {
      setDateRange(selectedRange.getRange(customStartDate || undefined, customEndDate || undefined));
    } else {
      setDateRange(selectedRange.getRange());
    }
  }, [
    selectedRange,
    selectedDay,
    customStartDate,
    customEndDate,
    payrollFrequency,
    payrollStartDate,
    customPeriod1Start,
    customPeriod1End,
    customPeriod2Start,
    customPeriod2End
  ]);

  // Load entries when date range changes
  useEffect(() => {
    loadEntries();
  }, [dateRange, selectedBusiness?.id]);

  // Load payroll settings when component mounts
  useEffect(() => {
    if (selectedBusiness?.id) {
      loadPayrollSettings();
    }
  }, [selectedBusiness?.id]);

  const loadEntries = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getPayrollEntries(
        selectedBusiness.id,
        format(dateRange.start, 'yyyy-MM-dd'),
        format(dateRange.end, 'yyyy-MM-dd')
      );
      setEntries(data as PayrollEntry[]);
      setError(null);
    } catch (err) {
      console.error('Error loading payroll entries:', err);
      setError(err instanceof Error ? err.message : 'Failed to load payroll entries');
    } finally {
      setLoading(false);
    }
  };

  const loadPayrollSettings = async () => {
    try {
      const settings = await getPayrollSettings(selectedBusiness!.id);
      if (settings) {
        setPayrollFrequency(settings.frequency);
        setPayrollStartDate(settings.start_date);
        if (settings.custom_period_1_start) setCustomPeriod1Start(settings.custom_period_1_start);
        if (settings.custom_period_1_end) setCustomPeriod1End(settings.custom_period_1_end);
        if (settings.custom_period_2_start) setCustomPeriod2Start(settings.custom_period_2_start);
        if (settings.custom_period_2_end) setCustomPeriod2End(settings.custom_period_2_end);
      }
    } catch (error) {
      console.error('Error loading payroll settings:', error);
      toast.error('Failed to load payroll settings');
    }
  };

  // Edit handlers
  const handleEditEntry = (entry: PayrollEntry) => {
    setEditingEntry(entry);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (entryId: string, updates: any) => {
    try {
      await updatePayrollEntry(entryId, updates);
      toast.success('Payroll entry updated successfully');
      
      // Reload entries to refresh the display
      await loadEntries();
    } catch (error) {
      console.error('Error updating payroll entry:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleDeleteEntry = async (entryId: string) => {
    try {
      await deletePayrollEntry(entryId);
      toast.success('Payroll entry deleted successfully');
      
      // Reload entries to refresh the display
      await loadEntries();
    } catch (error) {
      console.error('Error deleting payroll entry:', error);
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEntry(null);
  };

  const handleSaveSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const settings: PayrollSettings = {
        business_id: selectedBusiness.id,
        frequency: payrollFrequency,
        start_date: payrollStartDate,
        ...(payrollFrequency === 'custom' ? {
          custom_period_1_start: customPeriod1Start,
          custom_period_1_end: customPeriod1End,
          custom_period_2_start: customPeriod2Start,
          custom_period_2_end: customPeriod2End,
        } : {})
      };

      await savePayrollSettings(settings);
      setSaveSuccess(true);
      toast.success('Payroll settings saved successfully');
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error saving payroll settings:', error);
      toast.error('Failed to save payroll settings');
    }
  };

  const toggleResource = (resourceId: string) => {
    setExpandedResources(prev => {
      const next = new Set(prev);
      if (next.has(resourceId)) {
        next.delete(resourceId);
      } else {
        next.add(resourceId);
      }
      return next;
    });
  };

  // Group entries by resource and booking
  const entriesByResource = entries.reduce<Record<string, {
    resource: {
      first_name: string;
      last_name: string;
      email: string;
      compensation_type: string;
      flat_rate_amount: number | null;
      hourly_rate: number | null;
      avatar_url?: string;
    };
    timeclock: {
      entries: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    }>;
    bonuses: {
      entries: PayrollEntry[];
      totalEarnings: number;
    };
    totalHours: number;
    totalEarnings: number;
  }>>((acc, entry) => {
    if (!acc[entry.resource_id]) {
      acc[entry.resource_id] = {
        resource: {
          first_name: entry.resource_first_name,
          last_name: entry.resource_last_name,
          email: entry.resource_email,
          compensation_type: entry.compensation_type,
          flat_rate_amount: entry.flat_rate_amount,
          hourly_rate: entry.hourly_rate,
          avatar_url: entry.resource_avatar_url
        },
        timeclock: {
          entries: [],
          totalHours: 0,
          totalEarnings: 0
        },
        bookings: {},
        bonuses: {
          entries: [],
          totalEarnings: 0
        },
        totalHours: 0,
        totalEarnings: 0
      };
    }

    if (entry.entry_type === 'timeclock') {
      // Add to timeclock entries
      acc[entry.resource_id].timeclock.entries.push(entry);
      acc[entry.resource_id].timeclock.totalHours += entry.hours || 0;
      acc[entry.resource_id].timeclock.totalEarnings += entry.earnings || 0;
    } else if (entry.entry_type === 'bonus') {
      // Add to bonus entries (separate category)
      acc[entry.resource_id].bonuses.entries.push(entry);
      acc[entry.resource_id].bonuses.totalEarnings += entry.earnings || 0;
    } else {
      // Add to booking entries (flat_rate and commission)
      if (entry.booking_id && !acc[entry.resource_id].bookings[entry.booking_id]) {
        acc[entry.resource_id].bookings[entry.booking_id] = {
          booking_number: entry.booking_number || 0,
          booking_title: entry.booking_title || '',
          start_date: entry.start_date || entry.created_at,
          end_date: entry.end_date || entry.created_at,
          items: [],
          totalHours: 0,
          totalEarnings: 0
        };
      }

      if (entry.booking_id) {
        acc[entry.resource_id].bookings[entry.booking_id].items.push(entry);
        acc[entry.resource_id].bookings[entry.booking_id].totalHours += entry.hours || 0;
        
        // Calculate earnings for booking totals
        let bookingEarnings = 0;
        if (entry.entry_type === 'flat_rate') {
          bookingEarnings = (entry.hours || 0) * (entry.flat_rate_amount || 0);
        } else if (entry.entry_type === 'commission') {
          bookingEarnings = entry.earnings || 0;
        }
        acc[entry.resource_id].bookings[entry.booking_id].totalEarnings += bookingEarnings;
      }
    }

    // Update resource totals
    acc[entry.resource_id].totalHours += entry.hours || 0;
    
    // Calculate earnings based on entry type for resource totals
    let entryEarnings = 0;
    if (entry.entry_type === 'flat_rate') {
      entryEarnings = (entry.hours || 0) * (entry.flat_rate_amount || 0);
    } else if (entry.entry_type === 'timeclock') {
      entryEarnings = (entry.hours || 0) * (entry.hourly_rate || 0);
    } else if (entry.entry_type === 'commission') {
      entryEarnings = entry.earnings || 0;
    } else if (entry.entry_type === 'bonus') {
      entryEarnings = entry.earnings || 0; // Bonus entries store earnings directly
    }
    
    acc[entry.resource_id].totalEarnings += entryEarnings;

    return acc;
  }, {});

  return (
    <div className="bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/50" data-manage-payroll-page>
      <style jsx global>{`
        /* Hide scrollbar for payroll content */
        .payroll-scroll-container::-webkit-scrollbar {
          display: none;
        }
        
        .payroll-scroll-container {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className="px-4 sm:px-6 py-4">
        <div className="space-y-4">
          {/* Main Content Card */}
          <div className="bg-white/70 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl">
            {/* Search Header */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Search Bar */}
                <div className="relative flex-1 max-w-md">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search entries..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 text-sm bg-white/60 backdrop-blur-sm border border-white/30 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 transition-all duration-200"
                  />
                </div>

                {/* Filters and Buttons */}
                <div className="flex items-center gap-2">
                  {/* Date Range Selector */}
                  <div className="relative">
                    <button
                      onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-300 transition-all duration-200"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">{selectedRange.label}</span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    
                    {isDateRangeOpen && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl py-2 z-50 animate-in slide-in-from-top-2">
                    {DATE_RANGES.map((range) => (
                      <button
                        key={range.value}
                        onClick={() => {
                          setSelectedRange(range);
                          if (range.value === 'select_day') {
                            setIsDatePickerOpen(true);
                          } else if (range.value === 'custom_range') {
                            setIsCustomRangePickerOpen(true);
                          } else {
                            setIsDateRangeOpen(false);
                          }
                        }}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors duration-150 ${
                          selectedRange.value === range.value
                            ? 'bg-purple-50 text-purple-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span>{range.label}</span>
                          {range.value === 'select_day' && selectedDay && (
                            <span className="text-xs text-gray-500">
                              {format(selectedDay, 'MMM d')}
                            </span>
                          )}
                          {range.value === 'custom_range' && customStartDate && customEndDate && (
                            <span className="text-xs text-gray-500">
                              {format(customStartDate, 'MMM d')} - {format(customEndDate, 'MMM d')}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Date Pickers */}
                {isDatePickerOpen && (
                  <div className="absolute right-0 top-full mt-2 p-4 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl z-50">
                    <input
                      type="date"
                      value={selectedDay ? format(selectedDay, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                        setSelectedDay(date);
                        setIsDatePickerOpen(false);
                        setIsDateRangeOpen(false);
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                    />
                  </div>
                )}
                
                {isCustomRangePickerOpen && (
                  <div className="absolute right-0 top-full mt-2 p-6 bg-white/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl z-50 w-80">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                        <input
                          type="date"
                          value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                          onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                            setCustomStartDate(date);
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                        <input
                          type="date"
                          value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                          onChange={(e) => {
                            const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                            setCustomEndDate(date);
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                        />
                      </div>
                      <div className="flex justify-end pt-2">
                        <button
                          onClick={() => {
                            setIsCustomRangePickerOpen(false);
                            setIsDateRangeOpen(false);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-colors duration-150"
                        >
                          Apply Range
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                  </div>

                  {/* Settings Button - Icon only */}
                  <button 
                    onClick={() => setIsSettingsModalOpen(true)}
                    className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-300 transition-all duration-200 flex-shrink-0"
                  >
                    <Settings className="h-4 w-4 text-gray-600" />
                  </button>
                  
                  {/* Payroll Request Button */}
                  <button 
                    onClick={() => setIsPayrollRequestsModalOpen(true)}
                    className="px-3 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-white" />
                      <span className="text-sm font-medium text-white hidden xl:inline">Payroll Request</span>
                    </div>
                  </button>
                  
                  {/* Run Payroll Button */}
                  <button 
                    onClick={() => {
                      // TODO: Add run payroll functionality
                      console.log('Run Payroll clicked');
                    }}
                    className="px-3 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4 text-white" />
                      <span className="text-sm font-semibold text-white hidden xl:inline">Run Payroll</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="p-4">
              {loading ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center">
                    <div className="relative">
                      <div className="w-16 h-16 mx-auto mb-6">
                        <div className="absolute inset-0 rounded-full border-4 border-purple-100"></div>
                        <div className="absolute inset-0 rounded-full border-4 border-purple-600 border-t-transparent animate-spin"></div>
                      </div>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Payroll Data</h3>
                    <p className="text-gray-600">Gathering earnings and time entries...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="p-4">
                  <div className="max-w-md mx-auto text-center">
                    <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                      <X className="h-8 w-8 text-red-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button 
                      onClick={loadEntries}
                      className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-xl hover:bg-purple-200 transition-colors duration-150"
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              ) : Object.keys(entriesByResource).length === 0 ? (
                <div className="flex items-center justify-center h-96">
                  <div className="text-center max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl flex items-center justify-center">
                      <Clock className="h-10 w-10 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">No Payroll Data</h3>
                    <p className="text-gray-600 leading-relaxed">
                      No completed jobs or time entries found for the selected period. 
                      Earnings from completed bookings and time clock entries will appear here.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-gray-100/50">
                  {Object.entries(entriesByResource).map(([resourceId, data]) => (
                    <PayrollResourceSummary
                      key={resourceId}
                      resourceId={resourceId}
                      data={data}
                      isExpanded={expandedResources.has(resourceId)}
                      onToggle={() => toggleResource(resourceId)}
                      onToggleBooking={(bookingId) => {
                        setExpandedBookings(prev => {
                          const next = new Set(prev);
                          if (next.has(bookingId)) {
                            next.delete(bookingId);
                          } else {
                            next.add(bookingId);
                          }
                          return next;
                        });
                      }}
                      expandedBookings={expandedBookings}
                      onEditEntry={handleEditEntry}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* Settings Modal */}
      {isSettingsModalOpen && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-3xl max-h-[90vh] sm:max-h-[85vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Payroll Settings</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Configure pay periods and processing preferences</p>
                </div>
                <button 
                  onClick={() => setIsSettingsModalOpen(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-4 sm:p-8 overflow-y-auto max-h-[calc(90vh-140px)] sm:max-h-[calc(85vh-120px)]">
              <div className="space-y-8">
                {/* Payroll Period Settings */}
                <div className="bg-gradient-to-r from-purple-50/50 to-indigo-50/50 rounded-2xl p-6 border border-purple-100/50">
                  <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-purple-500/10 rounded-lg">
                      <Calendar className="h-5 w-5 text-purple-600" />
                    </div>
                    Pay Period Configuration
                  </h4>
                  
                  <div className="space-y-6">
                    {/* Frequency Selection */}
                    <div>
                      <label htmlFor="payroll-frequency" className="block text-sm font-medium text-gray-700 mb-3">
                        Payroll Frequency
                      </label>
                      <select
                        id="payroll-frequency"
                        value={payrollFrequency}
                        onChange={(e) => setPayrollFrequency(e.target.value)}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                      >
                        <option value="weekly">Weekly</option>
                        <option value="bi-weekly">Bi-Weekly</option>
                        <option value="semi-monthly">Semi-Monthly (15th & End of Month)</option>
                        <option value="monthly">Monthly</option>
                        <option value="custom">Custom Periods</option>
                      </select>
                      <p className="mt-2 text-xs text-gray-500">
                        {payrollFrequency === 'weekly' && 'Pay employees every week'}
                        {payrollFrequency === 'bi-weekly' && 'Pay employees every two weeks'}
                        {payrollFrequency === 'semi-monthly' && 'Pay employees on the 15th and last day of month'}
                        {payrollFrequency === 'monthly' && 'Pay employees once per month'}
                        {payrollFrequency === 'custom' && 'Define custom pay periods'}
                      </p>
                    </div>
                    
                    {/* Custom Period Settings */}
                    {payrollFrequency === 'custom' && (
                      <div className="space-y-6 bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                        <div>
                          <h6 className="text-sm font-medium text-gray-700 mb-3">First Period</h6>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="period1-start" className="block text-xs text-gray-500 mb-2">Start Day</label>
                              <input
                                id="period1-start"
                                type="number"
                                min="1"
                                max="31"
                                value={customPeriod1Start}
                                onChange={(e) => setCustomPeriod1Start(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                              />
                            </div>
                            <div>
                              <label htmlFor="period1-end" className="block text-xs text-gray-500 mb-2">End Day</label>
                              <input
                                id="period1-end"
                                type="number"
                                min="1"
                                max="31"
                                value={customPeriod1End}
                                onChange={(e) => setCustomPeriod1End(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <h6 className="text-sm font-medium text-gray-700 mb-3">Second Period</h6>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="period2-start" className="block text-xs text-gray-500 mb-2">Start Day</label>
                              <input
                                id="period2-start"
                                type="number"
                                min="1"
                                max="31"
                                value={customPeriod2Start}
                                onChange={(e) => setCustomPeriod2Start(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                              />
                            </div>
                            <div>
                              <label htmlFor="period2-end" className="block text-xs text-gray-500 mb-2">End Day</label>
                              <input
                                id="period2-end"
                                type="number"
                                min="1"
                                max="31"
                                value={customPeriod2End}
                                onChange={(e) => setCustomPeriod2End(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Start Date */}
                    <div>
                      <label htmlFor="payroll-start-date" className="block text-sm font-medium text-gray-700 mb-3">
                        First Pay Period Start Date
                      </label>
                      <input
                        id="payroll-start-date"
                        type="date"
                        value={payrollStartDate}
                        onChange={(e) => setPayrollStartDate(e.target.value)}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-300 bg-white/60 backdrop-blur-sm"
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        This is the date your first pay period begins
                      </p>
                    </div>
                    
                    {/* Period Preview */}
                    <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Upcoming Pay Periods</h5>
                      <div className="text-xs text-gray-600 space-y-2">
                        {payrollFrequency === 'weekly' && (
                          <>
                            <div className="flex justify-between py-1">
                              <span>Period 1:</span>
                              <span className="font-medium">
                                {format(new Date(payrollStartDate), 'MMM d')} - {format(new Date(new Date(payrollStartDate).getTime() + 6 * 24 * 60 * 60 * 1000), 'MMM d, yyyy')}
                              </span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span>Period 2:</span>
                              <span className="font-medium">
                                {format(new Date(new Date(payrollStartDate).getTime() + 7 * 24 * 60 * 60 * 1000), 'MMM d')} - {format(new Date(new Date(payrollStartDate).getTime() + 13 * 24 * 60 * 60 * 1000), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </>
                        )}
                        {payrollFrequency === 'bi-weekly' && (
                          <>
                            <div className="flex justify-between py-1">
                              <span>Period 1:</span>
                              <span className="font-medium">
                                {format(parseISO(payrollStartDate), 'MMM d')} - {format(addDays(parseISO(payrollStartDate), 13), 'MMM d, yyyy')}
                              </span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span>Period 2:</span>
                              <span className="font-medium">
                                {format(addDays(parseISO(payrollStartDate), 14), 'MMM d')} - {format(addDays(parseISO(payrollStartDate), 27), 'MMM d, yyyy')}
                              </span>
                            </div>
                          </>
                        )}
                        {payrollFrequency === 'semi-monthly' && (
                          <>
                            <div className="flex justify-between py-1">
                              <span>Period 1:</span>
                              <span className="font-medium">1st - 15th of each month</span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span>Period 2:</span>
                              <span className="font-medium">16th - end of each month</span>
                            </div>
                          </>
                        )}
                        {payrollFrequency === 'custom' && (
                          <>
                            <div className="flex justify-between py-1">
                              <span>Period 1:</span>
                              <span className="font-medium">{customPeriod1Start}th - {customPeriod1End}th of each month</span>
                            </div>
                            <div className="flex justify-between py-1">
                              <span>Period 2:</span>
                              <span className="font-medium">{customPeriod2Start}th - {customPeriod2End}th of each month</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Future Settings Categories */}
                <div className="bg-gradient-to-r from-gray-50/50 to-indigo-50/50 rounded-2xl p-6 border border-gray-200/50">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Additional Settings</h4>
                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-center gap-3 p-3 bg-white/40 rounded-xl">
                      <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      <span>Tax settings and deductions</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/40 rounded-xl">
                      <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      <span>Direct deposit and payment methods</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/40 rounded-xl">
                      <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      <span>Approval workflows</span>
                    </div>
                  </div>
                </div>
                
                {/* Success Message */}
                {saveSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl text-sm animate-in slide-in-from-top-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                      Payroll settings saved successfully!
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-4 sm:px-8 py-4 sm:py-6 border-t border-gray-100/50 bg-gradient-to-r from-gray-50/50 to-white/50">
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
                <button
                  onClick={() => setIsSettingsModalOpen(false)}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white/60 backdrop-blur-sm border border-gray-200 rounded-xl hover:bg-white/80 transition-all duration-150 w-full sm:w-auto"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>, 
        document.body
      )}

      {/* Payroll Requests Modal - Full Screen */}
      {isPayrollRequestsModalOpen && createPortal(
        <div className="fixed inset-0 bg-white z-[9999] flex flex-col">
          {/* Modal Header */}
          <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Payroll Requests</h2>
                <p className="text-sm text-gray-600 mt-1">Review and approve employee payroll requests</p>
              </div>
              <button 
                onClick={() => setIsPayrollRequestsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-150"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="flex-1 overflow-hidden">
            <PayrollRequestsSection 
              businessId={selectedBusiness?.id || ''}
              onRequestProcessed={loadEntries}
            />
          </div>
        </div>,
        document.body
      )}

      {/* PayrollEditModal */}
      <PayrollEditModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        entry={editingEntry}
        onSave={handleSaveEdit}
        onDelete={handleDeleteEntry}
      />
    </div>
  );
}